import { createSelector, createSlice, PayloadAction, original } from '@reduxjs/toolkit';
import { BundleItemDataExtended, BundleMetadata, BundleUndoRedoHistory } from '@/api';
import { type RootState, type BundleTypeKeys, BundleType } from '@/store';
import { selectBundlePageIds } from './bundleSections';

interface BundleState {
  bundlePagesData: Map<string, BundleItemDataExtended>;
  bundleMetadata: BundleMetadata;
  bundleUndoRedoHistory: BundleUndoRedoHistory;
}

const initialState: BundleState = {
  bundlePagesData: new Map(),
  bundleMetadata: {
    bundleType: null,
    caseId: null,
    otherBundleId: null,
  },
  bundleUndoRedoHistory: {
    undone: 0,
    applied: 0,
  },
};

const bundleSlice = createSlice({
  name: 'bundle',
  initialState: {
    [BundleType.LIGHT]: initialState,
    [BundleType.FULL]: initialState,
  } as Record<BundleTypeKeys, BundleState>,
  reducers: {
    setBundlePagesData: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        data: Map<string, BundleItemDataExtended>;
      }>,
    ) => {
      const { key, data } = action.payload;
      state[key].bundlePagesData = data;
    },
    setBundleMetadata: (state, action: PayloadAction<{ key: BundleTypeKeys; data: BundleMetadata }>) => {
      const { key, data } = action.payload;
      state[key].bundleMetadata = data;
    },
    setBundleUndoRedoHistory: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        data: BundleUndoRedoHistory;
      }>,
    ) => {
      const { key, data } = action.payload;
      state[key].bundleUndoRedoHistory = data;
    },
    /**
     * Optimistic-update helper: set `pageDimensions.pageRotation` for a single page
     * without refetching the entire bundle. Used by the rotate-page mutation so the
     * PDF re-renders rotated the moment the user clicks, then the mutation
     * reconciles with the server (or rolls back) in the background.
     *
     * NOTE: this project does NOT register Immer's `enableMapSet()` plugin, so
     * even READING from a drafted Map (e.g. `state[key].bundlePagesData.get(id)`)
     * throws — Immer auto-drafts the Map on property access and the Map proxy
     * needs the plugin. We use `original()` to read the un-drafted Map, build
     * the replacement Map outside the draft, and then do a plain property
     * assignment back onto the slice (which Immer handles without the plugin —
     * the existing `setBundlePagesData` reducer relies on the same pattern).
     */
    setBundlePageRotation: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        pageId: string;
        pageRotation: number;
      }>,
    ) => {
      const { key, pageId, pageRotation } = action.payload;
      const originalSlice = original(state[key]);
      const currentMap = originalSlice?.bundlePagesData;
      if (!currentMap) {
        return;
      }
      const currentPage = currentMap.get(pageId);
      if (!currentPage) {
        return;
      }
      /**
       * If the rotation transitions between an "upright" orientation (0° / 180°)
       * and a "sideways" orientation (90° / 270°), the effective page width and
       * height swap — a portrait page becomes landscape and vice versa. The
       * backend reports the post-rotation dimensions on the next /bundle fetch,
       * but for the optimistic phase we need to do the same swap locally so the
       * surrounding row container resizes to match the rotated canvas instead
       * of overflowing into the next page.
       */
      const previousRotation = currentPage.pageDimensions.pageRotation ?? 0;
      const wasUpright = previousRotation % 180 === 0;
      const willBeUpright = pageRotation % 180 === 0;
      const shouldSwapDimensions = wasUpright !== willBeUpright;
      const nextMap = new Map(currentMap);
      nextMap.set(pageId, {
        ...currentPage,
        pageDimensions: {
          ...currentPage.pageDimensions,
          width: shouldSwapDimensions ? currentPage.pageDimensions.height : currentPage.pageDimensions.width,
          height: shouldSwapDimensions ? currentPage.pageDimensions.width : currentPage.pageDimensions.height,
          pageRotation,
        },
      });
      state[key].bundlePagesData = nextMap;
    },
    /**
     * Apply the blank flags returned by the mark blank / mark not-blank mutations
     * so the thumbnails reflect the new state without refetching the whole bundle.
     * Both bundle types are patched because a page id is shared between them.
     *
     * Uses the same `original()` + plain-assignment pattern as
     * `setBundlePageRotation` because this project does NOT register Immer's
     * `enableMapSet()` plugin (see the note on that reducer).
     */
    setBundlePagesBlankState: (state, action: PayloadAction<{ pages: { pageId: string; isBlank: boolean }[] }>) => {
      const { pages } = action.payload;

      if (!pages.length) {
        return;
      }

      Object.values(BundleType).forEach((key) => {
        const originalSlice = original(state[key]);
        const currentMap = originalSlice?.bundlePagesData;
        if (!currentMap?.size) {
          return;
        }

        const nextMap = new Map(currentMap);
        let hasChanges = false;

        pages.forEach(({ pageId, isBlank }) => {
          const page = currentMap.get(pageId);
          if (!page || page.isBlank === isBlank) {
            return;
          }

          nextMap.set(pageId, { ...page, isBlank });
          hasChanges = true;
        });

        if (hasChanges) {
          state[key].bundlePagesData = nextMap;
        }
      });
    },
    /**
     * Remove the given chronology entry ids from every page's `chronologyEntryIds`
     * in both bundle types. Used when a chronology entry is deleted so the
     * thumbnail "in chronology" indicator clears immediately without waiting for
     * the /bundle refetch to land.
     *
     * Uses the same `original()` + plain-assignment pattern as
     * `setBundlePageRotation` because this project does NOT register Immer's
     * `enableMapSet()` plugin (see the note on that reducer).
     */
    removeChronologyEntriesFromBundlePages: (state, action: PayloadAction<{ entryIds: string[] }>) => {
      const entryIdsToRemove = new Set(action.payload.entryIds);

      if (!entryIdsToRemove.size) {
        return;
      }

      Object.values(BundleType).forEach((key) => {
        const originalSlice = original(state[key]);
        const currentMap = originalSlice?.bundlePagesData;
        if (!currentMap?.size) {
          return;
        }

        const nextMap = new Map(currentMap);
        let hasChanges = false;

        currentMap.forEach((page, pageId) => {
          if (!page.chronologyEntryIds?.length) {
            return;
          }

          const nextChronologyEntryIds = page.chronologyEntryIds.filter((entryId) => !entryIdsToRemove.has(entryId));
          if (nextChronologyEntryIds.length === page.chronologyEntryIds.length) {
            return;
          }

          nextMap.set(pageId, {
            ...page,
            chronologyEntryIds: nextChronologyEntryIds.length ? nextChronologyEntryIds : null,
          });
          hasChanges = true;
        });

        if (hasChanges) {
          state[key].bundlePagesData = nextMap;
        }
      });
    },
  },
});

export const {
  setBundlePagesData,
  setBundleMetadata,
  setBundleUndoRedoHistory,
  setBundlePageRotation,
  setBundlePagesBlankState,
  removeChronologyEntriesFromBundlePages,
} = bundleSlice.actions;

// RTK SELECTORS WITHOUT CALLBACK

export const selectBundlePagesData = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundle[activeBundleType].bundlePagesData : initialState.bundlePagesData;
};

export const selectBundleUndoRedoHistory = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundle[activeBundleType].bundleUndoRedoHistory : initialState.bundleUndoRedoHistory;
};

// CUSTOM SELECTORS WITHOUT CALLBACK

/**
 * with key
 * const pagesData = useAppSelector(selectBundlePagesDataByStoreKey(BundleType.LIGHT));
 * without key
 * const pagesDataDefault = useAppSelector(selectBundlePagesDataByStoreKey());
 */
export const selectBundlePagesDataByStoreKey = (bundleKey?: BundleTypeKeys) => (state: RootState) => {
  const resolvedBundleKey = bundleKey ?? state.sharedData.activeBundleType;
  return resolvedBundleKey ? state.bundle[resolvedBundleKey].bundlePagesData : initialState.bundlePagesData;
};

/**
 * with key
 * const metadata = useAppSelector(selectBundleMetadataByStoreKey(BundleType.LIGHT));
 * without key
 * const metadataDefault = useAppSelector(selectBundleMetadataByStoreKey());
 */
export const selectBundleMetadataByStoreKey = (bundleKey?: BundleTypeKeys) => (state: RootState) => {
  const resolvedBundleKey = bundleKey ?? state.sharedData.activeBundleType;
  return resolvedBundleKey ? state.bundle[resolvedBundleKey].bundleMetadata : initialState.bundleMetadata;
};

// CUSTOM SELECTORS WITH CALLBACK

/**
 * Get pageData using pageId
 */
export const makeSelectBundlePageDataByPageId = createSelector([selectBundlePagesData], (bundlePagesData) => {
  return (pageId: string) => bundlePagesData.get(pageId);
});

/**
 * Get pageData using pageIndex
 */
export const makeSelectBundlePageDataByPageIndex = createSelector(
  [selectBundlePagesData, selectBundlePageIds],
  (bundlePagesData, bundlePageIds) => {
    return (pageIndex: number) => bundlePagesData?.get(bundlePageIds[pageIndex]);
  },
);

export const bundleReducer = bundleSlice.reducer;
